/**
 * constants.ts
 *
 * Constants and the persisted-settings type for the data table EC. Defines the
 * `PersistentDataTableSettings` shape that gets serialised per-table along with
 * its default value, plus virtualization, sizing, and timing tunables.
 *
 * See also:
 * - @./types/types.ts - Atoms that hold `PersistentDataTableSettings`.
 * - @./components/DataTable.tsx - Primary consumer of these constants.
 */

import type { TableState } from "@tanstack/react-table";

export const DATA_TABLE_SETTINGS_VERSION = 2;
export const TABLE_SELECT_COLUMN_ID = "select";
/** Synthetic merged gutter column (improvements bundle) — row number + focus + select. */
export const TABLE_FOCUS_COLUMN_ID = "focus_pin";
/** Synthetic stacked "Actions" column (improvements bundle) — hosts the collapsed action columns. */
export const TABLE_ACTIONS_COLUMN_ID = "actions_stack";
export const TABLE_SEARCH_RESULTS_COLUMN_ID = "SEARCH_RESULTS_COLUMN";
export const ISOLATED_EDGES_SORT_COLUMN_ID = "isolatedEdges";

export type PersistentDataTableSettings = Pick<
  TableState,
  | "expanded"
  // | "sorting"
  | "columnOrder"
  | "columnPinning"
  | "grouping"
  | "columnSizing"
  | "columnSizingInfo"
> & {
  version?: typeof DATA_TABLE_SETTINGS_VERSION;
  /** Row sizing for PIVOT mode - maps row PK to width */
  rowSizing?: Record<string, number>;
  /**
   * Collapse the action columns into the focus gutter (improvements grammar).
   * Collapsed is the default — read through {@link isActionCollapsePreferred}
   * so settings persisted before this field existed also read as collapsed.
   */
  actionColumnsCollapsed?: boolean;
  /**
   * Per-column collapse overrides keyed by action column id. Columns absent
   * from the map follow the bundle-wide `actionColumnsCollapsed` default —
   * read through {@link isActionColumnCollapsed}. Bulk collapse/expand resets
   * this map so those actions always apply to every column.
   */
  actionColumnCollapse?: Record<string, boolean>;
};

/** Collapsed is the default: a never-toggled setting (undefined) reads as collapsed. */
export function isActionCollapsePreferred(actionColumnsCollapsed: boolean | undefined): boolean {
  return actionColumnsCollapsed ?? true;
}

/**
 * Whether one action column is collapsed into the gutter: its explicit
 * per-column override wins, otherwise the bundle-wide default applies.
 */
export function isActionColumnCollapsed(
  columnId: string,
  settings: Pick<PersistentDataTableSettings, "actionColumnsCollapsed" | "actionColumnCollapse">
): boolean {
  return (
    settings.actionColumnCollapse?.[columnId] ??
    isActionCollapsePreferred(settings.actionColumnsCollapsed)
  );
}

export const DEFAULT_DATA_TABLE_SETTINGS: PersistentDataTableSettings = Object.freeze({
  // sorting: [],
  expanded: {},
  columnOrder: [],
  grouping: [],
  columnPinning: {
    left: [TABLE_SELECT_COLUMN_ID],
    right: [],
  },
  columnSizing: {},
  columnSizingInfo: {
    startOffset: null,
    startSize: null,
    deltaOffset: null,
    deltaPercentage: null,
    isResizingColumn: false as false | string,
    columnSizingStart: [],
  },
  version: DATA_TABLE_SETTINGS_VERSION,
  rowSizing: {},
  actionColumnsCollapsed: true,
});

export const DATA_TABLE_VIRTUALIZER_OVER_SCAN_ROWS = 6;

export const DATA_TABLE_VIRTUALIZER_OVER_SCAN_COLUMNS = 1;

export const DATA_TABLE_MAX_VIRTUAL_ROWS = 240;

export const DATA_TABLE_MAX_VIRTUAL_COLUMNS = 80;

// oxlint-disable-next-line regex/invalid
export const ASYNC_FILTER_IS_DEBUG = false;

// oxlint-disable-next-line regex/invalid
export const ASYNC_FILTER_IS_VERBOSE = false;

export const ROW_SIZE = 32;

/**
 * Width of the legacy standalone select-checkbox gutter (improvements flag
 * off). Under improvements the merged focus/select gutter uses
 * MERGED_GUTTER_WIDTH (rowSelectionAndFocusColumn.ts) instead.
 */
export const GUTTER_COLUMN_WIDTH = 32;

/**
 * Width of a standalone (expanded) action column — icon-only, so kept narrow.
 * Exactly twice the header buttons' right-edge center inset
 * (LINKED_COLUMN_SAVE_ICON_PADDING 6 + LINKED_COLUMN_SAVE_ICON_SIZE/2 7), so
 * the hover-revealed "⋮" and sort buttons sit centered with equal space
 * either side.
 */
export const ACTION_COLUMN_WIDTH = 26;

/** Icon size (px) for action-column cells — expanded columns and the stacked "Actions" column alike. */
export const ACTION_ICON_SIZE = 16;

/**
 * Alpha for an action icon whose action can't run on the row (e.g. fly-to with
 * no geometry): near-invisible, but present so the column keeps its rhythm.
 */
export const DISABLED_ACTION_ICON_ALPHA = 0.06;

export const HEADER_ROW_SIZE = 54; // Increased to accommodate column header and search input

export const TABLE_CONTEXT_MENU_ICON_CLASS_NAME = "size-5";

export const SCROLL_TO_HIGHLIGHT_DURATION = 4000;
